import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { PageHeader, Spinner, HelpIcon } from "@/components/Atoms";
import {
    ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
    PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
    Cloud, HardDrive, Server, Building2, Cpu, Boxes, Database, Workflow,
    ShieldCheck, Mail, Wrench, Activity, RefreshCw, Presentation, Phone,
    AlertTriangle, Gauge, Puzzle, Network, Zap,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const ACCENT = "#CCFF00";
const PIE_COLORS = ["#CCFF00", "#00c2a8", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#64748b", "#22c55e", "#eab308"];
const STATUS_COLORS = {
    active: "#22c55e", error: "#ef4444", pending: "#f59e0b", provisioning: "#CCFF00",
    deleting: "#64748b", succeeded: "#22c55e", failed: "#ef4444", canceled: "#64748b",
    running: "#CCFF00", queued: "#f59e0b", unknown: "#475569",
};

const toSeries = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name, value }));
const num = (n) => (n ?? 0).toLocaleString("en-US");
const gb = (n) => `${num(Math.round(n ?? 0))} GB`;
const fmtClock = (ts) => new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// ---- small building blocks -------------------------------------------------
function Kpi({ icon: Icon, label, value, sub, tone = "default", hint, testid }) {
    const toneCls = {
        default: "text-cm-text", good: "text-cm-success",
        warn: "text-cm-warning", bad: "text-cm-danger", accent: "text-cm-cyan",
    }[tone];
    return (
        <div className="cm-card p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-cm-muted">
                <Icon size={14} />
                <span className="cm-label">{label}</span>
                {hint && <span className="ml-auto"><HelpIcon text={hint} testid={testid && `${testid}-hint`} /></span>}
            </div>
            <div className={`mt-2 text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
            {sub && <div className="text-xs text-cm-muted mt-0.5 font-mono">{sub}</div>}
        </div>
    );
}

function Panel({ title, icon: Icon, right, hint, children, testid, className = "" }) {
    return (
        <div className={`cm-card ${className}`} data-testid={testid}>
            <div className="px-4 py-2.5 border-b border-cm-line flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    {Icon && <Icon size={14} className="text-cm-brand" />}
                    <span className="text-xs font-mono uppercase tracking-widest text-cm-text">{title}</span>
                    {hint && <HelpIcon text={hint} testid={testid && `${testid}-hint`} />}
                </div>
                {right}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

const tooltipStyle = {
    contentStyle: { background: "#0B0E14", border: "1px solid #242B3B", fontSize: 12, borderRadius: 0 },
    labelStyle: { color: "#8B95A8" }, itemStyle: { color: "#E6EAF2" },
};
const tickStyle = { fontSize: 11, fill: "#6B7488" };

function RangeBtns({ value, onChange, testidPrefix }) {
    const opts = [[6, "6h"], [24, "24h"], [168, "7d"], [720, "30d"]];
    return (
        <div className="flex">
            {opts.map(([h, label]) => (
                <button
                    key={h}
                    data-testid={`${testidPrefix}-${h}`}
                    onClick={() => onChange(h)}
                    className={`px-2 py-1 text-xs font-mono uppercase tracking-widest border ${value === h ? "border-cm-brand text-cm-brand bg-cm-brand/10" : "border-cm-line2 text-cm-muted hover:text-cm-text"}`}
                >{label}</button>
            ))}
        </div>
    );
}

function Donut({ data, testid }) {
    const total = data.reduce((s, d) => s + d.value, 0);
    if (!total) return <div className="text-xs text-cm-dim py-8 text-center">No data</div>;
    return (
        <div data-testid={testid}>
            <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {data.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name] || PIE_COLORS[i % PIE_COLORS.length]} stroke="#05070A" />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

function Bars({ data, color = ACCENT, testid }) {
    if (!data.length) return <div className="text-xs text-cm-dim py-8 text-center">No data</div>;
    return (
        <div data-testid={testid}>
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" tick={tickStyle} />
                    <YAxis type="category" dataKey="name" width={110} tick={tickStyle} />
                    <Tooltip {...tooltipStyle} cursor={{ fill: "#11151D" }} />
                    <Bar dataKey="value" fill={color} radius={[0, 2, 2, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// ---- fleet resource trend --------------------------------------------------
function ResourceTrend() {
    const [hours, setHours] = useState(24);
    const { data } = useSWR(`/api/admin/statistics/history/resources?hours=${hours}`, fetcher, { refreshInterval: 60000 });
    const points = (data?.points || []).map((p) => ({
        t: fmtClock(p.ts),
        cpu: p.cpu_avg, ram: p.ram_avg, req: p.req_total, storage: p.storage_gb,
    }));
    return (
        <Panel title="Fleet utilization (time series)" icon={Activity} testid="stats-resource-trend"
            hint="Computed from per-cloud metrics (cloud_metrics). Avg CPU % / Avg RAM % = average ACROSS ALL clouds per time bucket (~60 buckets over the selected range). Σ req/s = sum of all cloud request rates. Σ storage = sum of used storage."
            right={<RangeBtns value={hours} onChange={setHours} testidPrefix="stats-range" />}>
            {!data ? <Spinner /> : !points.length ? (
                <div className="text-xs text-cm-dim py-10 text-center">No metric samples in this window yet.</div>
            ) : (
                <>
                    <div className="cm-label mb-1">Avg CPU % / Avg RAM %</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={points} margin={{ left: 0, right: 8 }}>
                            <defs>
                                <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#CCFF00" stopOpacity={0.4} /><stop offset="100%" stopColor="#CCFF00" stopOpacity={0} /></linearGradient>
                                <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00c2a8" stopOpacity={0.35} /><stop offset="100%" stopColor="#00c2a8" stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1F2B" />
                            <XAxis dataKey="t" tick={tickStyle} minTickGap={30} />
                            <YAxis tick={tickStyle} width={36} domain={[0, 100]} />
                            <Tooltip {...tooltipStyle} />
                            <Area type="monotone" dataKey="cpu" name="CPU %" stroke="#CCFF00" fill="url(#gc)" strokeWidth={1.5} />
                            <Area type="monotone" dataKey="ram" name="RAM %" stroke="#00c2a8" fill="url(#gr)" strokeWidth={1.5} />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div className="cm-label mt-3 mb-1">Σ Request rate / Σ Storage (GB)</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <LineChart data={points} margin={{ left: 0, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1F2B" />
                            <XAxis dataKey="t" tick={tickStyle} minTickGap={30} />
                            <YAxis tick={tickStyle} width={36} />
                            <Tooltip {...tooltipStyle} />
                            <Line type="monotone" dataKey="req" name="Req/s" stroke="#f59e0b" dot={false} strokeWidth={1.5} />
                            <Line type="monotone" dataKey="storage" name="Storage GB" stroke="#8b5cf6" dot={false} strokeWidth={1.5} />
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-cm-dim mt-1 font-mono">{num(data.total_samples)} samples · {points.length} buckets</div>
                </>
            )}
        </Panel>
    );
}

// ---- live services (auto-refreshing) --------------------------------------
function LiveBadge({ isValidating, testid }) {
    return (
        <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-cm-success" data-testid={testid}>
            <span className={`w-1.5 h-1.5 rounded-full bg-emerald-500 ${isValidating ? "animate-ping" : "animate-pulse"}`} /> Live
        </span>
    );
}

function LiveServices() {
    // LIVE: auto-poll every 15s (guest-agent probe of Collabora + Talk VMs).
    const { data, isValidating, mutate } = useSWR(
        "/api/admin/statistics/live-services", fetcher,
        { refreshInterval: 15000, revalidateOnFocus: true, dedupingInterval: 10000 });
    const c = data?.collabora, t = data?.talk;
    return (
        <Panel title="Live services — active sessions" icon={Presentation} testid="stats-live-services"
            hint="Polled LIVE every ~15 s from the service VMs via qemu guest agent: Collabora /hosting/metrics (active docs/views) and Talk signaling :8080 /api/v1/stats + /metrics. Talk: sessions = connected clients, rooms = open conversations, 'in call' = participants actively sending media. Best effort — unreachable VMs show an error."
            right={
                <div className="flex items-center gap-2">
                    <LiveBadge isValidating={isValidating} testid="stats-live-indicator" />
                    <button data-testid="stats-live-refresh" onClick={() => mutate()} disabled={isValidating}
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-1.5 disabled:opacity-40">
                        <RefreshCw size={11} className={isValidating ? "animate-spin" : ""} /> Now
                    </button>
                </div>
            }>
            {!data ? <Spinner label="Querying service VMs…" /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-cm-line p-3" data-testid="stats-live-collabora">
                        <div className="flex items-center gap-2 text-cm-text text-xs mb-2"><Presentation size={13} className="text-cm-brand" /> Collabora
                            <HelpIcon testid="stats-live-collabora-hint" text="Active documents = authoritative from coolwsds admin metric /cool/getMetrics (document_active_views_all_count total/average). This is NOT the kit process count: Collabora keeps a pre-spawned kit pool (kit_spare_*) — that is diagnostics only ('kits'), not the number of open documents." />
                        </div>
                        <div className="flex gap-6">
                            <div><div className="text-2xl font-semibold text-cm-cyan tabular-nums">{num(c.active_documents_total)}</div><div className="text-xs text-cm-muted font-mono uppercase">active documents</div></div>
                            <div><div className="text-2xl font-semibold text-cm-text tabular-nums">{num(c.active_views_total)}</div><div className="text-xs text-cm-muted font-mono uppercase">active views</div></div>
                            <div><div className="text-2xl font-semibold text-cm-muted tabular-nums">{num(c.kit_processes_total)}</div><div className="text-xs text-cm-dim font-mono uppercase">kit pool (diag.)</div></div>
                        </div>
                        <div className="mt-2 text-xs text-cm-muted font-mono">{c.reachable}/{c.vm_count} VMs reachable</div>
                        <div className="mt-2 space-y-1">
                            {c.vms.map((v, i) => (
                                <div key={i} className="text-xs font-mono flex justify-between gap-2">
                                    <span className="text-cm-muted truncate">{v.host}</span>
                                    {v.error ? <span className="text-cm-danger truncate" title={v.error}>{v.error}</span>
                                        : <span className="text-cm-success">{num(v.active_documents)} docs · {num(v.kit_processes)} kits</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="border border-cm-line p-3" data-testid="stats-live-talk">
                        <div className="flex items-center gap-2 text-cm-text text-xs mb-2"><Phone size={13} className="text-[#00c2a8]" /> Talk (HPB signaling)</div>
                        <div className="flex gap-6 flex-wrap">
                            <div title="Clients connected to the signaling (chat + presence)."><div className="text-2xl font-semibold text-[#00c2a8] tabular-nums">{num(t.active_sessions_total)}</div><div className="text-xs text-cm-muted font-mono uppercase">Sessions</div></div>
                            <div title="Open Talk conversations/rooms."><div className="text-2xl font-semibold text-cm-text tabular-nums">{num(t.active_rooms_total)}</div><div className="text-xs text-cm-muted font-mono uppercase">Rooms</div></div>
                            <div title="People currently sending media (camera/screen/audio) — MCU publishers. 0 = nobody in a live call; sessions are only connected for chat/signaling."><div className="text-2xl font-semibold text-cm-text tabular-nums">{num(t.active_streams_total)}</div><div className="text-xs text-cm-muted font-mono uppercase">in call</div></div>
                        </div>
                        <div className="mt-2 text-xs text-cm-muted font-mono">
                            {t.vm_count === 0 ? "No Talk HPB service VMs present." : `${t.reachable}/${t.vm_count} VMs reachable`}
                        </div>
                        <div className="mt-2 space-y-1">
                            {(t.vms || []).map((v, i) => (
                                <div key={i} className="text-xs font-mono flex justify-between gap-2">
                                    <span className="text-cm-muted truncate">{v.host}</span>
                                    {v.error ? <span className="text-cm-danger truncate" title={v.error}>{v.error}</span>
                                        : <span className="text-cm-success">{num(v.active_sessions)} sess. · {num(v.active_rooms)} rooms · {num(v.active_streams)} calls</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
}

// ---- Talk HPB: connected clouds (which customers are on the signaling) -----
function TalkActiveBackends() {
    const { data, isValidating, mutate } = useSWR(
        "/api/admin/statistics/talk/active-backends", fetcher,
        { refreshInterval: 15000, revalidateOnFocus: true, dedupingInterval: 10000 });
    const nav = useNavigate();
    const backends = data?.backends || [];
    const knownClouds = backends.filter((b) => b.cloud_id);
    const unknownHosts = backends.filter((b) => !b.cloud_id);
    if (!data) return null;
    return (
        <div className="cm-card mb-4" data-testid="stats-talk-backends">
            <div className="px-4 py-2.5 border-b border-cm-line flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Cloud size={14} className="text-cm-brand" />
                    <span className="text-xs font-mono uppercase tracking-widest text-cm-text">
                        Connected clouds ({knownClouds.length})
                    </span>
                    <HelpIcon align="left"
                        text={`Talk HPB backends known to the signaling server (from /metrics signaling_http_client_pool_connections). ${data.reachable}/${data.vm_count} signaling VM(s) reachable.`} />
                </div>
                <div className="flex items-center gap-2">
                    <LiveBadge isValidating={isValidating} />
                    <button onClick={() => mutate()} disabled={isValidating}
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-1.5 disabled:opacity-40">
                        <RefreshCw size={11} className={isValidating ? "animate-spin" : ""} /> Now
                    </button>
                </div>
            </div>
            <div className="p-3">
                {knownClouds.length === 0 && unknownHosts.length === 0 ? (
                    <div className="text-xs text-cm-dim py-4 text-center">No backends detected. Either no clouds have Talk HPB enabled, or the signaling VM is unreachable.</div>
                ) : (
                    <div className="cm-table-wrap" style={{ maxHeight: 320, overflowY: "auto" }}>
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Cloud</th>
                                    <th className="cm-th">Domain</th>
                                    <th className="cm-th text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {knownClouds.map((b) => (
                                    <tr key={b.cloud_id || b.host} className="cm-tr cursor-pointer"
                                        onClick={() => nav(`/clouds/${b.cloud_id}`)}>
                                        <td className="cm-td font-mono text-xs text-cm-text max-w-[220px] truncate">
                                            {b.cloud_name || b.host}
                                        </td>
                                        <td className="cm-td text-[11px] font-mono text-cm-muted max-w-[260px] truncate">
                                            {b.host}
                                        </td>
                                        <td className="cm-td text-right">
                                            <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase rounded ${b.cloud_status === "active" ? "bg-emerald-950 text-emerald-400" : b.cloud_status === "suspended" ? "bg-amber-950 text-amber-400" : "bg-zinc-800 text-zinc-400"}`}>
                                                {b.cloud_status || "?"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {unknownHosts.map((b) => (
                                    <tr key={b.host} className="cm-tr">
                                        <td className="cm-td text-[11px] text-cm-muted italic" colSpan={3}>
                                            {b.host} &mdash; not registered as a cloud
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- Collabora + Talk metrics trend (60s ClickHouse time-series) ----------
function ServiceMetricsTrend() {
    const [hours, setHours] = useState(6);
    const { data } = useSWR(
        `/api/admin/statistics/services/history?hours=${hours}`, fetcher,
        { refreshInterval: 60000 });
    const points = (data?.points || []).map((p) => ({
        t: new Date((p.t || "").replace(" ", "T") + "Z").toLocaleTimeString(
            "en-GB", { hour: "2-digit", minute: "2-digit" }),
        docs: p.collab_active_documents, views: p.collab_active_views,
        mem: p.collab_memory_mb, kits: p.collab_kit_processes,
        sessions: p.talk_sessions, rooms: p.talk_rooms,
        inCall: p.talk_in_call, streams: p.talk_streams,
    }));
    return (
        <Panel title="Collabora & Talk — time series" icon={Activity} testid="stats-service-trend"
            hint="60-second samples of the live services (ClickHouse time series, bucketed to ~500 points). Collabora: active documents (authoritative from coolwsds admin metric, NOT the kit process count) + views. Talk: sessions, rooms, in-call (actively sending participants)."
            right={<RangeBtns value={hours} onChange={setHours} testidPrefix="svc-metrics-range" />}>
            {!data ? <Spinner /> : !points.length ? (
                <div className="text-xs text-cm-dim py-10 text-center" data-testid="svc-metrics-empty">
                    {data?.error ? `Time series unavailable (${data.error}).` : "No service samples in this window yet (sampler runs every 60 s)."}
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 text-cm-text text-xs mb-1"><Presentation size={13} className="text-cm-brand" /> Collabora — active documents / views</div>
                    <ResponsiveContainer width="100%" height={160}>
                        <AreaChart data={points} margin={{ left: 0, right: 8 }}>
                            <defs>
                                <linearGradient id="gdoc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#CCFF00" stopOpacity={0.4} /><stop offset="100%" stopColor="#CCFF00" stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1F2B" />
                            <XAxis dataKey="t" tick={tickStyle} minTickGap={30} />
                            <YAxis tick={tickStyle} width={36} allowDecimals={false} />
                            <Tooltip {...tooltipStyle} />
                            <Area type="monotone" dataKey="docs" name="Active documents" stroke="#CCFF00" fill="url(#gdoc)" strokeWidth={1.5} />
                            <Line type="monotone" dataKey="views" name="Active views" stroke="#00c2a8" dot={false} strokeWidth={1.2} />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-2 text-cm-text text-xs mt-3 mb-1"><Phone size={13} className="text-[#00c2a8]" /> Talk — sessions / rooms / in call</div>
                    <ResponsiveContainer width="100%" height={160}>
                        <LineChart data={points} margin={{ left: 0, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1F2B" />
                            <XAxis dataKey="t" tick={tickStyle} minTickGap={30} />
                            <YAxis tick={tickStyle} width={36} allowDecimals={false} />
                            <Tooltip {...tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#8b5cf6" dot={false} strokeWidth={1.5} />
                            <Line type="monotone" dataKey="rooms" name="Rooms" stroke="#f59e0b" dot={false} strokeWidth={1.5} />
                            <Line type="monotone" dataKey="inCall" name="In call" stroke="#22c55e" dot={false} strokeWidth={1.8} />
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-cm-dim mt-1 font-mono">{points.length} buckets · {data.bucket_seconds}s resolution</div>
                </>
            )}
        </Panel>
    );
}


// ---- agents (with orphan cleanup) ------------------------------------------
function AgentsPanel({ agents, onPruned }) {
    const a = agents || {};
    const [busy, setBusy] = useState(false);
    const prune = useCallback(async () => {
        setBusy(true);
        try {
            const dry = await api.post("/api/admin/statistics/agents/prune?dry_run=true");
            const n = dry.data?.candidates || 0;
            if (n === 0) { alert("No orphaned agents to clean up."); return; }
            if (!window.confirm(`Remove ${n} orphaned/stale agent registration(s) from the DB?\n(Cloud deleted / no assignment / heartbeat > 7 days. NO VMs are deleted.)`)) return;
            const res = await api.post("/api/admin/statistics/agents/prune?dry_run=false");
            alert(`${res.data?.deleted || 0} agent row(s) cleaned.`);
            onPruned && onPruned();
        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    }, [onPruned]);
    const Stat = ({ label, value, tone }) => (
        <div className="flex-1 min-w-[70px]">
            <div className={`text-xl font-semibold tabular-nums ${tone || "text-cm-text"}`}>{num(value)}</div>
            <div className="text-xs text-cm-muted font-mono uppercase tracking-widest">{label}</div>
        </div>
    );
    return (
        <Panel title="Agents" icon={Cpu} testid="stats-agents"
            hint="VM agents on web/worker VMs. 'online' = current heartbeat. 'orphaned' = cloud deleted or no cloud assignment. 'cleanable' = orphaned OR heartbeat older than 7 days — removable from the DB via the button (deletes NO VMs)."
            right={
                <button data-testid="stats-agents-prune" onClick={prune} disabled={busy || !a.prunable}
                    className="cm-btn-secondary cm-btn-sm flex items-center gap-1.5 !text-cm-warning disabled:opacity-30">
                    <Wrench size={11} className={busy ? "animate-spin" : ""} /> Clean orphans{a.prunable ? ` (${num(a.prunable)})` : ""}
                </button>
            }>
            <div className="flex flex-wrap gap-4">
                <Stat label="online" value={a.online} tone="text-cm-success" />
                <Stat label="offline" value={a.offline} tone={a.offline ? "text-cm-warning" : "text-cm-text"} />
                <Stat label="registered" value={a.total} />
                <Stat label="orphaned" value={a.orphaned} tone={a.orphaned ? "text-cm-danger" : "text-cm-text"} />
                <Stat label="cleanable" value={a.prunable} tone={a.prunable ? "text-cm-warning" : "text-cm-text"} />
            </div>
        </Panel>
    );
}

function PoolsTable({ pools }) {
    const rows = [
        ["DB (master LB)", pools.db, Database],
        ["Redis", pools.redis, Boxes],
        ["S3 / Object", pools.s3, HardDrive],
        ["Collabora", pools.collabora, Presentation],
        ["Talk HPB", pools.talk_hpb, Phone],
    ];
    return (
        <Panel title="Capacity pools" icon={Boxes} testid="stats-pools"
            hint="Per pool type: used = Σ current_clouds of all pools, capacity = Σ max_clouds. Utilization = used/capacity (>90% red, >70% amber).">
            <table className="w-full text-xs font-mono">
                <thead><tr className="text-cm-muted text-[11px] uppercase tracking-widest text-left">
                    <th className="pb-2">Pool</th><th className="pb-2 text-right">Pools</th><th className="pb-2 text-right">Used</th><th className="pb-2 text-right">Capacity</th><th className="pb-2 text-right">Utilization</th>
                </tr></thead>
                <tbody>
                    {rows.map(([label, p, Icon]) => {
                        const pct = p.max_clouds ? Math.round(100 * p.current_clouds / p.max_clouds) : 0;
                        return (
                            <tr key={label} className="border-t border-cm-line" data-testid={`stats-pool-${label}`}>
                                <td className="py-1.5 text-cm-text flex items-center gap-1.5"><Icon size={12} className="text-cm-muted" />{label}</td>
                                <td className="py-1.5 text-right text-cm-muted">{p.pools}</td>
                                <td className="py-1.5 text-right text-cm-text">{num(p.current_clouds)}</td>
                                <td className="py-1.5 text-right text-cm-muted">{num(p.max_clouds)}</td>
                                <td className="py-1.5 text-right">
                                    <span className={pct >= 90 ? "text-cm-danger" : pct >= 70 ? "text-cm-warning" : "text-cm-success"}>{pct}%</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Panel>
    );
}

// ---- page ------------------------------------------------------------------
function PmgStatsPanel() {
    const { data, error } = useSWR("/api/admin/statistics/pmg-history?hours=24", fetcher, { refreshInterval: 60000 });
    if (error) return <Panel title="PMG mail throughput" icon={Mail} testid="stats-pmg"><div className="text-xs text-cm-danger">Unable to load PMG stats: {String(error)}</div></Panel>;
    const samples = data?.samples || [];
    if (samples.length === 0) {
        return (
            <Panel title="PMG mail throughput (0 samples)" icon={Mail} testid="stats-pmg" hint="Mail queue data of the PMG shields. The first sample appears after ~5 minutes.">
                <div className="text-xs text-cm-dim">No data yet — PMG stats sampler is starting.</div>
            </Panel>
        );
    }

    const last = samples[samples.length - 1];
    const maxDeferred = Math.max(...samples.map((s) => s.deferred || 0), 1);
    const maxIncoming = Math.max(...samples.map((s) => s.incoming || 0), 1);

    return (
        <div data-testid="stats-pmg">
            <Panel title={`PMG mail throughput (${samples.length} samples)`} icon={Mail} testid="stats-pmg-chart" hint={`Queue depth over ${samples.length > 1 ? `${Math.round((new Date(samples[samples.length-1].ts) - new Date(samples[0].ts)) / 3600000)}h` : "<1h"}. Active = being delivered, deferred = delayed/waiting.`}>
            <div className="flex gap-4 mb-3">
                <span className="text-xs font-mono">
                    <span className="text-sky-300">active: {num(last.active)}</span>
                    {" · "}
                    <span className={last.deferred > 500 ? "text-orange-300" : "text-cm-muted"}>deferred: {num(last.deferred)}</span>
                    {" · "}
                    <span className="text-cm-muted">incoming: {num(last.incoming)}</span>
                    {" · as of "}{last.ts ? fmtClock(last.ts) : "—"}
                </span>
            </div>
            <div className="relative h-40 w-full">
                {samples.map((s, i) => {
                    const xPct = samples.length > 1 ? (i / (samples.length - 1)) * 100 : 50;
                    const aH = maxDeferred > 0 ? ((s.active || 0) / maxDeferred) * 100 : 0;
                    const dH = maxDeferred > 0 ? ((s.deferred || 0) / maxDeferred) * 100 : 0;
                    const iH = maxIncoming > 0 ? ((s.incoming || 0) / maxIncoming) * 100 : 0;
                    return (
                        <div key={i} className="absolute bottom-0 flex items-end gap-[1px]" style={{ left: `${xPct}%`, transform: "translateX(-50%)" }}
                            title={`${s.ts ? fmtClock(s.ts) : ""}: active=${num(s.active)} deferred=${num(s.deferred)} incoming=${num(s.incoming)}`}>
                            <div style={{ height: `${Math.max(aH, 2)}%`, width: 4 }} className="bg-sky-500/60 rounded-t-sm" />
                            <div style={{ height: `${Math.max(dH, 2)}%`, width: 4 }} className="bg-orange-500/60 rounded-t-sm" />
                            <div style={{ height: `${Math.max(iH, 2)}%`, width: 3 }} className="bg-zinc-600/60 rounded-t-sm" />
                        </div>
                    );
                })}
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-cm-muted font-mono">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-500/60 rounded-sm" /> active</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500/60 rounded-sm" /> deferred</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-zinc-600/60 rounded-sm" /> incoming</span>
            </div>
            </Panel>
        </div>
    );
}

export default function StatisticsPage() {
    const { data, error, isLoading, mutate } = useSWR("/api/admin/statistics", fetcher, {
        refreshInterval: 30000, revalidateOnFocus: false,
    });

    if (isLoading && !data) return <div className="p-6"><Spinner label="Loading statistics…" /></div>;
    if (error) return <div className="p-6 text-red-400 text-sm">Error loading: {String(error)}</div>;
    const d = data || {};
    const cl = d.clouds || {}, sv = d.service_vms || {}, px = d.proxmox || {};
    const jobs = d.jobs || {}, bk = d.backups || {}, mon = d.monitoring || {}, us = d.usage || {};

    return (
        <div className="p-4 md:p-6 space-y-6" data-testid="statistics-page">
            <PageHeader
                title="Statistics"
                subtitle={`Platform overview · as of ${fmtClock(d.generated_at || Date.now())} · cache ${d.cache_age_s ?? 0}s`}
                action={
                    <button data-testid="stats-refresh-btn" onClick={() => mutate()}
                        className="cm-btn-secondary cm-btn-sm flex items-center gap-2">
                        <RefreshCw size={12} /> Refresh
                    </button>
                }
                testid="stats-header"
            />

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                <Kpi icon={Cloud} label="Clouds" value={num(cl.total)} sub={`${num((cl.by_status || {}).active)} active · ${num((cl.by_status || {}).error)} errors`} tone="accent" hint="Total number of managed clouds. 'active' = status=active, 'errors' = status=error." testid="kpi-clouds" />
                <Kpi icon={HardDrive} label="Storage used" value={gb(cl.storage_used_gb)} sub={`of ${gb(cl.storage_alloc_gb)} · ${cl.storage_pct || 0}%`} hint="Σ used storage (used_storage_gb) of all clouds vs. total allocated storage (storage_gb). % = used / allocated." testid="kpi-storage" />
                <Kpi icon={Server} label="Service VMs" value={`${num(sv.healthy)}/${num(sv.total)}`} sub={`healthy · +${num((sv.cloud_vms || {}).total)} cloud VMs`} tone={sv.total && sv.healthy === sv.total ? "good" : (sv.unhealthy ? "warn" : "default")} hint="Healthy / all SHARED service VMs (Collabora, Talk HPB, Redis, Caddy, Egress, Antivirus, Push …). Per-cloud web/worker VMs are counted SEPARATELY as 'cloud VMs'." testid="kpi-svms" />
                <Kpi icon={Building2} label="Resellers" value={num((d.resellers || {}).active)} sub={`${num((d.resellers || {}).total)} total`} hint="Active (status=active) / total registered resellers." testid="kpi-resellers" />
                <Kpi icon={Cpu} label="Agents online" value={num((d.agents || {}).online)} sub={`${num((d.agents || {}).total)} registered · ${num((d.agents || {}).offline)} offline`} tone={(d.agents || {}).offline ? "warn" : "good"} hint="VM agents with status=online (current heartbeat) / all registered agents." testid="kpi-agents" />
                <Kpi icon={Network} label="Proxmox" value={`${num(px.nodes_online)}/${num(px.nodes_total)}`} sub={`${num(px.cpu_cores_total)} cores · ${gb((px.ram_mb_total || 0) / 1024)}`} hint="Online / total hypervisor nodes in the cluster." testid="kpi-proxmox" />
                <Kpi icon={Workflow} label="Active jobs" value={num(jobs.running + jobs.queued)} sub={`${num(jobs.failed_24h)} failures/24h`} tone={jobs.failed_24h ? "warn" : "default"} hint="Running + queued jobs. 'Failures/24h' = failed jobs in the last 24 hours." testid="kpi-jobs" />
            </div>

            {/* live services — LIVE, at the top (most important values first) */}
            <LiveServices />

            {/* Talk HPB — connected clouds */}
            <TalkActiveBackends />

            {/* Collabora + Talk 60s time series (ClickHouse) */}
            <ServiceMetricsTrend />

            {/* Capacity pools + backups/usage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PoolsTable pools={d.pools || {}} />
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Kpi icon={ShieldCheck} label="Backups ok/24h" value={num(bk.ok_24h)} sub={`${num(bk.fail_24h)} failed · ${num(bk.running)} running`} tone={bk.fail_24h ? "warn" : "good"} hint="Successful backup runs in the last 24 h (status done/success)." testid="kpi-backups" />
                        <Kpi icon={Gauge} label="Requests (month)" value={num(us.requests_total)} sub={`${num(us.traffic_gb_total)} GB traffic`} hint="Σ requests of all clouds in the current billing month (usage_records)." testid="kpi-requests" />
                        <Kpi icon={Zap} label="Autoscaling" value={num((d.autoscaling || {}).enabled)} sub={`${num((d.autoscaling || {}).policies)} policies`} hint="Enabled autoscaling policies / total configured policies." testid="kpi-autoscale" />
                        <Kpi icon={Puzzle} label="Custom apps" value={num((d.apps || {}).custom_enabled)} sub={`${num((d.apps || {}).custom_total)} total`} hint="Enabled / total existing custom apps (not deleted)." testid="kpi-apps" />
                        <Kpi icon={Mail} label="Domains" value={num((d.domains || {}).total)} sub={`${num((d.domains || {}).dns_validated)} DNS ok`} hint="Registered domains total; 'DNS ok' = successfully validated DNS entries." testid="kpi-domains" />
                        <Kpi icon={Network} label="SDN zones" value={num(d.sdn_zones)} hint="Number of configured SDN network zones (software-defined networking on Proxmox)." testid="kpi-sdn" />
                    </div>
                    <Panel title="Feature adoption (clouds)" icon={Boxes} testid="stats-features" hint="Number of clouds where the respective feature is enabled (options): Collabora, Antivirus, AI, High-Performance-Backend (Talk), Autoscaling.">
                        <Bars data={toSeries(cl.features)} color="#8b5cf6" testid="chart-features" />
                    </Panel>
                </div>
            </div>

            {/* charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Panel title="Clouds by status" icon={Cloud} testid="stats-clouds-status" hint="Distribution of ALL clouds over their lifecycle status (active, error, pending, provisioning, deleting …)."><Donut data={toSeries(cl.by_status)} testid="chart-clouds-status" /></Panel>
                <Panel title="Service VMs by kind" icon={Server} testid="stats-svm-kind" hint="Number of SHARED service VMs per kind (Collabora, Redis, Egress Gateway, Caddy, Antivirus, Push …). Per-cloud web/worker VMs are deliberately EXCLUDED."><Bars data={toSeries(sv.by_kind)} testid="chart-svm-kind" /></Panel>
                <Panel title="Jobs by status" icon={Workflow} testid="stats-jobs-status" hint="All jobs in the job queue grouped by status (succeeded, failed, running, queued, canceled)."><Donut data={toSeries(jobs.by_status)} testid="chart-jobs-status" /></Panel>
            </div>

            <ResourceTrend />

            {/* monitoring + proxmox nodes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title={`Monitoring alerts (${num(mon.alerts_total)})`} icon={AlertTriangle} testid="stats-alerts">
                    {(mon.recent || []).length === 0 ? <div className="text-xs text-cm-dim">No alerts.</div> : (
                        <div className="space-y-1 max-h-72 overflow-y-auto">
                            {mon.recent.map((a, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs font-mono py-1 border-b border-cm-line" data-testid={`stats-alert-${i}`}>
                                    <span className={`px-1.5 py-0.5 text-[10px] uppercase ${["critical", "down"].includes(a.to_state) ? "bg-red-950 text-red-400" : a.to_state === "warning" ? "bg-amber-950 text-amber-400" : "bg-zinc-800 text-zinc-400"}`}>{a.to_state}</span>
                                    <span className="text-cm-text truncate flex-1">{a.name || a.kind}</span>
                                    <span className="text-cm-dim">{a.ts ? new Date(a.ts).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
                <Panel title="Proxmox nodes" icon={Network} testid="stats-proxmox-nodes">
                    <div className="space-y-1.5">
                        {(px.nodes || []).map((n, i) => (
                            <div key={i} className="flex items-center justify-between text-xs font-mono py-1 border-b border-cm-line" data-testid={`stats-node-${i}`}>
                                <span className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${n.online ? "bg-emerald-500" : "bg-red-500"}`} />
                                    <span className="text-cm-text">{n.name}</span>
                                    <span className="text-cm-dim">{n.role}</span>
                                </span>
                                <span className="text-cm-muted">{num(n.cpu_cores)} cores · {gb((n.ram_mb || 0) / 1024)}</span>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* PMG mail throughput */}
            <PmgStatsPanel />

            {/* agents + orphan cleanup */}
            <AgentsPanel agents={d.agents} onPruned={mutate} />
        </div>
    );
}
